"use client";

import Link from "next/link";
import { ArrowRight, Crown, Lock } from "lucide-react";
import RandomProblems from "@/components/RandomProblems";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useEffectivePass } from "@/hooks/useEffectivePass";
import type { QuizClass } from "@/lib/quiz-problems";

interface HighDifficultyQuizProps {
  quizClass: QuizClass;
}

const QUIZ_CONFIG: Record<
  QuizClass,
  {
    label: string;
    quizTitle: string;
    count: number;
    siblingHref: string;
    siblingLabel: string;
  }
> = {
  theory: {
    label: "이론",
    quizTitle: "실기 이론 매운감자퀴즈",
    count: 12,
    siblingHref: "/coding/potato-coding-hard-quiz",
    siblingLabel: "실기 코딩 매운감자 퀴즈",
  },
  coding: {
    label: "코딩",
    quizTitle: "실기 코딩 매운감자 퀴즈",
    count: 10,
    siblingHref: "/theory/potato-theory-hard-quiz",
    siblingLabel: "실기 이론 매운감자퀴즈",
  },
};

export default function HighDifficultyQuiz({
  quizClass,
}: HighDifficultyQuizProps) {
  const { isLoaded, isPremiumOrAbove } = useEffectivePass();
  const config = QUIZ_CONFIG[quizClass];

  if (!isLoaded) {
    return (
      <div
        className="not-prose my-8 rounded-xl border border-border bg-card p-4 sm:p-6"
        role="status"
        aria-busy="true"
        aria-live="polite"
      >
        <span className="sr-only">
          {config.quizTitle} 이용 권한을 확인하는 중입니다.
        </span>
        <div className="flex items-center gap-3" aria-hidden="true">
          <Skeleton className="h-11 w-11 shrink-0 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-5 w-44 max-w-full" />
            <Skeleton className="h-4 w-full max-w-md" />
          </div>
        </div>
        <Skeleton
          className="mt-5 h-10 w-full sm:w-40"
          aria-hidden="true"
        />
      </div>
    );
  }

  if (!isPremiumOrAbove) {
    return (
      <section
        className="not-prose my-8 flex flex-col items-center rounded-xl border border-amber-300 bg-amber-50 px-5 py-10 text-center dark:border-amber-800 dark:bg-amber-950/40 sm:px-8 sm:py-12"
        aria-labelledby={`${quizClass}-hard-quiz-lock-title`}
      >
        <span
          className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300"
          aria-hidden="true"
        >
          <Lock className="h-6 w-6" />
        </span>
        <p className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-amber-700 dark:text-amber-400">
          <Crown className="h-4 w-4" aria-hidden="true" />
          Premium 이상 전용
        </p>
        <h2
          id={`${quizClass}-hard-quiz-lock-title`}
          className="mt-2 text-balance text-xl font-bold text-foreground sm:text-2xl"
        >
          {config.quizTitle}는 Premium 이상 전용이에요
        </h2>
        <p className="mt-2 max-w-md text-pretty text-sm leading-6 text-muted-foreground">
          난이도 상으로 분류된 {config.label} 문제만 {config.count}개씩
          랜덤으로 풀며 실전 대비 실력을 점검해 보세요.
        </p>
        <Button asChild size="lg" className="mt-5 w-full sm:w-auto">
          <Link href="/membership">
            Premium 이용권 보기
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </Button>
      </section>
    );
  }

  return (
    <>
      <aside
        className="not-prose my-6 flex flex-col gap-3 rounded-xl border border-border bg-muted/40 p-4 sm:flex-row sm:items-center sm:justify-between"
        aria-label="다른 매운감자퀴즈 안내"
      >
        <div className="min-w-0">
          <p className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-amber-700 dark:text-amber-400">
            <Crown className="h-4 w-4" aria-hidden="true" />
            Premium · 난이도 상
          </p>
          <p className="mt-1 text-sm leading-6 text-muted-foreground">
            {config.quizTitle}와 함께 다른 영역도 이어서 점검해
            보세요.
          </p>
        </div>
        <Button asChild variant="outline" size="sm" className="w-full sm:w-auto">
          <Link href={config.siblingHref}>
            {config.siblingLabel}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </Button>
      </aside>

      <RandomProblems
        minDifficulty="상"
        quizClass={quizClass}
        count={config.count}
        scoreTitle={config.quizTitle}
      />
    </>
  );
}
